import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export default function CTA() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please fill in your name and email.");
      return;
    }
    setLoading(true);
    setTimeout(() => {
      toast.success("Thanks — we'll be in touch within one business day.");
      setForm({ name: "", email: "", company: "", message: "" });
      setLoading(false);
    }, 700);
  };

  return (
    <section
      id="contact"
      data-testid="cta-section"
      className="bg-white text-neutral-950 py-24 md:py-36 border-t border-neutral-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-neutral-500">
              05 — Talk to us
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter mt-6 leading-[1.05]">
              Let's design
              <br />
              <span className="text-neutral-400">your operating system.</span>
            </h2>
            <p className="text-base md:text-lg text-neutral-600 font-light leading-relaxed mt-8 max-w-md">
              Tell us about your business. We'll respond with a discovery call and
              an initial blueprint within one business day.
            </p>

            <div className="mt-10 space-y-3 text-sm text-neutral-600">
              <p>
                <span className="text-neutral-400 mr-3">✦</span>
                hello@noex.systems
              </p>
              <p>
                <span className="text-neutral-400 mr-3">✦</span>
                Implementations across 8+ industries
              </p>
              <p>
                <span className="text-neutral-400 mr-3">✦</span>
                Average time-to-deploy: 4–8 weeks
              </p>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            data-testid="lead-form"
            className="md:col-span-6 md:col-start-7 space-y-5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-neutral-500 mb-2 block">
                  Name *
                </label>
                <Input
                  data-testid="lead-form-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="h-12 rounded-none border-0 border-b border-neutral-300 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-neutral-900 text-base"
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-neutral-500 mb-2 block">
                  Email *
                </label>
                <Input
                  data-testid="lead-form-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="h-12 rounded-none border-0 border-b border-neutral-300 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-neutral-900 text-base"
                  placeholder="you@company.com"
                />
              </div>
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-neutral-500 mb-2 block">
                Company
              </label>
              <Input
                data-testid="lead-form-company"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="h-12 rounded-none border-0 border-b border-neutral-300 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-neutral-900 text-base"
                placeholder="Company name"
              />
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-neutral-500 mb-2 block">
                What do you need?
              </label>
              <Textarea
                data-testid="lead-form-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                className="rounded-none border-0 border-b border-neutral-300 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-neutral-900 text-base resize-none"
                placeholder="Briefly describe your operations & what you'd like to automate or customize."
              />
            </div>

            <Button
              type="submit"
              data-testid="lead-form-submit"
              disabled={loading}
              className="group w-full md:w-auto h-14 px-10 rounded-full bg-neutral-950 text-white hover:bg-neutral-800 active:scale-95 transition-all duration-300 text-sm font-medium tracking-wide"
            >
              {loading ? "Sending..." : (
                <span className="inline-flex items-center gap-2">
                  Get Custom Solution
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              )}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}
